import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Paperclip, FileText, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { fileViewUrl } from "@/lib/files";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function ChatTab({ order }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isStaff = ["admin", "support"].includes(user?.role);
  const fixedChannel = user?.role === "partner" ? "company" : user?.role === "driver" ? "driver" : user?.role === "client" ? "client" : null;
  const [channel, setChannel] = useState(fixedChannel || "company");
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const load = async (ch) => {
    try { const { data } = await api.get(`/orders/${order.id}/messages`, { params: { channel: ch } }); setMsgs(data); }
    catch (e) { setMsgs([]); }
  };
  // Poll so admin↔partner/driver messages appear in near real-time while logged in.
  useEffect(() => {
    load(channel);
    const id = setInterval(() => load(channel), 5000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, [channel, order.id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (attachment_doc_id = null) => {
    if (!text.trim() && !attachment_doc_id) return;
    setSending(true);
    try {
      await api.post(`/orders/${order.id}/messages`, { channel, text: text.trim() || null, attachment_doc_id });
      setText(""); load(channel);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const del = async (m) => {
    if (!window.confirm(t("chat.deleteConfirm"))) return;
    try { await api.delete(`/orders/${order.id}/messages/${m.id}`); load(channel); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", "order");
    fd.append("entity_id", order.id); fd.append("category", "chat_attachment");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await send(data.id);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl" data-testid="chat-tab">
      {isStaff && (
        <div className="flex gap-2 mb-4">
          {["company", "driver", "client"].map((c) => (
            <button key={c} onClick={() => setChannel(c)} data-testid={`chat-channel-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${channel === c ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {c === "company" ? "Azienda" : c === "driver" ? "Autista" : "Cliente / Supporto"}
            </button>
          ))}
        </div>
      )}
      {user?.role === "client" && (
        <div className="flex items-center gap-2 mb-4 text-sm text-slate-600 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2" data-testid="client-support-banner">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Supporto Rimpatrio24 · Assistenza 24/7
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col h-[480px]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {msgs.length === 0 && <p className="text-sm text-slate-400 text-center py-10">Nessun messaggio. Inizia la conversazione.</p>}
          {msgs.map((m) => {
            const mine = m.sender_id === user?.id;
            const canDelete = !m.is_deleted && (mine || user?.role === "admin");
            return (
              <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"} items-center gap-1`} data-testid="chat-message">
                {mine && canDelete && (
                  <button onClick={() => del(m)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 p-1" data-testid="chat-delete-btn"><Trash2 size={14} /></button>
                )}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.is_deleted ? "bg-slate-50 text-slate-400 italic" : mine ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-800"}`}>
                  {!mine && !m.is_deleted && <div className="text-xs font-medium opacity-70 mb-0.5">{m.sender_email} · {m.sender_role}</div>}
                  {m.is_deleted ? (
                    <p className="text-sm">{t("chat.deleted")}</p>
                  ) : (
                    <>
                      {m.text && <p className="text-sm whitespace-pre-wrap">{m.text}</p>}
                      {m.attachment_doc_id && (
                        <a href={fileViewUrl(m.attachment_doc_id)} target="_blank" rel="noreferrer"
                          className={`text-xs flex items-center gap-1 mt-1 ${mine ? "text-blue-100" : "text-blue-700"} hover:underline`}>
                          <FileText size={13} /> Allegato
                        </a>
                      )}
                    </>
                  )}
                  <div className={`text-[10px] mt-1 ${mine ? "text-blue-200" : "text-slate-400"}`}>{formatDateTime(m.created_at)}</div>
                </div>
                {!mine && canDelete && (
                  <button onClick={() => del(m)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-rose-600 p-1" data-testid="chat-delete-btn"><Trash2 size={14} /></button>
                )}
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="border-t border-slate-100 p-3 flex items-center gap-2">
          <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="chat-file-input" />
          <Button size="icon" variant="ghost" onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn"><Paperclip size={18} /></Button>
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Scrivi un messaggio…" data-testid="chat-input" />
          <Button onClick={() => send()} disabled={sending} className="bg-blue-900 hover:bg-blue-800" data-testid="chat-send-btn"><Send size={16} /></Button>
        </div>
      </div>
    </div>
  );
}
